import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import LiveDashboard from "./pages/LiveDashboard";
import Programs from "./pages/Programs";
import ProgramDetail from "./pages/ProgramDetail";
import UploadWizard from "./pages/UploadWizard";
import ValidationStaging from "./pages/ValidationStaging";
import Report from "./pages/Report";
import PublicReport from "./pages/PublicReport";
import DonorPortal from "./pages/DonorPortal";
import DonorProgramReport from "./pages/DonorProgramReport";
import PublicForm from "./pages/PublicForm";
import InviteAccept from "./pages/InviteAccept";
import Compare from "./pages/Compare";
import KeyDictionary from "./pages/KeyDictionary";
import TheoryOfChange from "./pages/TheoryOfChange";
import Onboarding from "./pages/Onboarding";
import Pricing from "./pages/Pricing";
import Settings from "./pages/Settings";
import AdminAnalytics from "./pages/AdminAnalytics";
import MyAnalytics from "./pages/MyAnalytics";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import PublicDashboardShare from "./pages/PublicDashboardShare";
import GeoMap from "./pages/GeoMap";
import "./App.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user && user.onboarding_complete === false) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user && user.onboarding_complete) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/onboarding" element={<OnboardingGate><Onboarding /></OnboardingGate>} />
          <Route path="/" element={<Protected><LiveDashboard /></Protected>} />
          <Route path="/programs" element={<Protected><Programs /></Protected>} />
          <Route path="/programs/:pid" element={<Protected><ProgramDetail /></Protected>} />
          <Route path="/programs/:pid/upload" element={<Protected><UploadWizard /></Protected>} />
          <Route path="/programs/:pid/uploads/:uid/validate" element={<Protected><ValidationStaging /></Protected>} />
          <Route path="/programs/:pid/report" element={<Protected><Report /></Protected>} />
          <Route path="/compare" element={<Protected><Compare /></Protected>} />
          <Route path="/key-dictionary" element={<Protected><KeyDictionary /></Protected>} />
          <Route path="/programs/:pid/theory-of-change" element={<Protected><TheoryOfChange /></Protected>} />
          <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/admin/analytics" element={<Protected><AdminAnalytics /></Protected>} />
          <Route path="/my-analytics" element={<Protected><MyAnalytics /></Protected>} />
          <Route path="/map" element={<Protected><GeoMap /></Protected>} />
          <Route path="/public/report/:token" element={<PublicReport />} />
          <Route path="/share/:token" element={<PublicReport />} />
          <Route path="/dashboard/share/:token" element={<PublicDashboardShare />} />
          <Route path="/donor/:token" element={<DonorPortal />} />
          <Route path="/donor/:token/program/:pid" element={<DonorProgramReport />} />
          <Route path="/forms/invite/:invite_token" element={<PublicForm />} />
          <Route path="/forms/:token" element={<PublicForm />} />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
